import { MessageContent } from '@/types/message'
import { Renderer } from './renderer'
// import starPng from '@/ai-agent/images/star.png'
import React from 'react'
import Block from './block'
import { observer } from 'mobx-react'
import { getStores } from '@/stores'
import RenderContainer from './render-container'

interface MessageProps {
  msgContent: MessageContent
  index: number
  onMounted?: () => void
  useTypingComplete: boolean
  onCreate?: () => void
}

const Message = (props: MessageProps) => {
  const { msgContent, useTypingComplete = true } = props
  const [currentRenderKey, setCurrentRenderKey] = React.useState('0.0')
  const [isInitData, setIsInitData] = React.useState(false)
  const renderRecordMap = React.useRef({})
  const [blockNodeRendered, setBlockNodeRendered] = React.useState(false)
  const { agentV2Store } = getStores()

  const isEnd = props.msgContent.placement === 'end'

  React.useEffect(() => {
    props?.onCreate?.()
  }, [])

  const onMounted = () => {
    props?.onMounted?.()
  }

  const onTypingCompleteOrMount = ({
    curIdx,
    arrLength,
    outIndex
  }: {
    curIdx: number
    arrLength: number
    outIndex: number
  }) => {
    if (curIdx === 0 && arrLength === 1) {
      setCurrentRenderKey('0.0')
      setBlockNodeRendered(true)
      onMounted()
      return
    }

    const nextIndex = curIdx + 1
    if (nextIndex < arrLength) {
      // 如果还有下一个组件，更新到同一组的下一个
      const renderKey = `${outIndex}.${nextIndex}`
      setCurrentRenderKey(renderKey)
    } else {
      const renderKey = `${outIndex + 1}.0`
      setCurrentRenderKey(renderKey)
    }
  }

  React.useEffect(() => {
    // 获取最后一个key
    const keys = Object.keys(renderRecordMap.current)
    const lastKey = keys.length > 0 ? keys[keys.length - 1] : null
    if (currentRenderKey === lastKey && currentRenderKey !== '0.0') {
      onMounted()
      setBlockNodeRendered(true)
    }
  }, [currentRenderKey])

  React.useEffect(() => {
    msgContent?.content.forEach((item, outIndex) => {
      if (item?.type === 'block') {
        item?.value?.forEach((_, index) => {
          renderRecordMap.current[outIndex + '.' + index] = false
        })
      }
    })
    setIsInitData(true)
  }, [msgContent?.content.length])

  // 获取当前消息的更新标记，用于精确更新
  const messageUpdateFlag = agentV2Store.getMessageUpdateFlag(msgContent.message_id)

  const nodes = React.useMemo(() => {
    // 从currentRenderKey中解析出当前渲染位置
    const [currentOuterIdx, currentInnerIdx] = currentRenderKey.split('.').map(Number)

    return msgContent?.content?.map((item, outIndex) => {
      // 只渲染当前外层索引及之前的块
      if (outIndex > currentOuterIdx && useTypingComplete) {
        return null
      }

      if (item?.type === 'block') {
        return (
          <Block
            blockNodeRendered={blockNodeRendered}
            blockItem={item}
            msgContent={msgContent}
            index={props.index}
            style={item?.style}
          >
            {item?.value?.map((node, index) => {
              // 判断是否应该渲染当前组件
              const shouldRender =
                outIndex < currentOuterIdx || // 前面组的所有组件都渲染
                (outIndex === currentOuterIdx && index <= currentInnerIdx) // 当前组只渲染到当前位置
              if (!shouldRender && useTypingComplete) return null

              if (node.type === 'container') {
                return (
                  <RenderContainer
                    useTypingComplete={useTypingComplete}
                    msgContent={msgContent}
                    style={node?.style}
                    nodeList={node.value}
                    onTypingComplete={() => {
                      onTypingCompleteOrMount({
                        curIdx: index,
                        arrLength: item?.value?.length,
                        outIndex
                      })
                    }}
                  />
                )
              }

              return (
                <Renderer
                  useTypingComplete={useTypingComplete}
                  //兼容打字机效果，在打字完成后，每个组件按顺序先后渲染
                  onMount={() => {
                    //如果是右侧的消息，无需走打字机渲染效果,调用onTypingCompleteOrMount函数是直接不计算渲染时间
                    if (isEnd) {
                      onTypingCompleteOrMount({
                        curIdx: index,
                        arrLength: item?.value?.length,
                        outIndex
                      })
                    }
                    //如果是非文本类型，也无需走打字机效果,通过onMount函数来计算所有节点的渲染完成结束时间
                    if (!['text', 'link', 'markdown'].includes(node.type)) {
                      onTypingCompleteOrMount({
                        curIdx: index,
                        arrLength: item?.value?.length,
                        outIndex
                      })
                    }
                  }}
                  onTypingComplete={() => {
                    if (isEnd) return
                    if (!useTypingComplete) return
                    onTypingCompleteOrMount({
                      curIdx: index,
                      arrLength: item?.value?.length,
                      outIndex
                    })
                  }}
                  msgNode={node}
                  msgContent={msgContent}
                />
              )
            })}
          </Block>
        )
      }
    })
  }, [currentRenderKey, messageUpdateFlag])

  if (!isInitData) {
    return null
  }

  return (
    <div
      style={{
        border: '1px solid transparent',
        marginBottom: '20px'
      }}
      data-node-message-id={msgContent.message_id}
    >
      {nodes}
    </div>
  )
}

export default observer(Message)
